/**
 * Flags are referred to by their original kebab-cased name.
 * This is for simplicity and standardization across the Node and React SDKs, but requires
 * opting out of the React SDK's default camelCase naming convention.
 * Ensure `useCamelCaseFlagKeys` is off in the relevant flag providers.
 *
 * Flags may be located across different LaunchDarkly projects:
 * @see https://app.launchdarkly.com/projects/default/flags
 * @see https://app.launchdarkly.com/projects/jane-manager/flags
 * @see https://app.launchdarkly.com/projects/jane-brand/flags
 */
export enum FLAGS {
  // ---Brands + Catalog Flags---
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/analytics-cookieless-token-auth */
  analyticsCookielessTokenAuth = 'analytics-cookieless-token-auth',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-bid-modifiers */
  jamBidControl = 'jam-bid-modifiers',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-campaign-management */
  jamCampaignManagement = 'jam-campaign-management',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-keyword-ads */
  jamKeywordAds = 'jam-keyword-ads',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-monthly-revenue-goal-type */
  jamMonthlyRevenueGoalType = 'jam-monthly-revenue-goal-type',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/pcat-state-specific-views */
  pcatStateViewEnabled = 'pcat-state-specific-views',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/pcat-localized-store-count */
  pcatLocalizedStoreCount = 'pcat-localized-store-count',

  // ---Cannabinerds Flags---
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-show-weekend-lookahead-controls */
  cnShowWeekendLookaheadControls = 'cn-show-weekend-lookahead-controls',
  /** @see https://app.launchdarkly.com/projects/default/flags/cn-special-detail-page-nsw */
  cnSpecialDetailPageNsw = 'cn-special-detail-page-nsw',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-show-kiosk-notifications-card */
  cnShowKioskNotificationsCard = 'cn-show-kiosk-notifications-card',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-skip-redundant-debounced-update */
  cnSkipRedundantDebouncedUpdate = 'cn-skip-redundant-debounced-update',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-store-based-product-review-flow */
  cnStoreBasedProductReviewFlow = 'cn-store-based-product-review-flow',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-global-product-review-flow */
  cnGlobalProductReviewFlow = 'cn-global-product-review-flow',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/cn-bulk-revert-product-images-descriptions
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-bulk-revert-product-images-descriptions
   */
  cnBulkRevertProductImagesDescriptions = 'cn-bulk-revert-product-images-descriptions',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-bulk-clear-product-potencies */
  cnBulkClearProductPotencies = 'cn-bulk-clear-product-potencies',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-bulk-edit-invalid-products */
  c1BulkEditInvalidProducts = 'c1-bulk-edit-invalid-products',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-retail-org-ui */
  cnRetailOrgUi = 'cn-retail-org-ui',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-annex-cloud-integration */
  cnAnnexCloudIntegration = 'cn-annex-cloud-integration',

  // ---Commerce 3 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-show-relieved-feeling-label */
  c3ShowRelievedFeelingLabel = 'c3-show-relieved-feeling-label',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-thematic-rows-context */
  c3ThematicRowsContext = 'c3-thematic-rows-context',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-enable-product-alerts-beta */
  c3EnableProductAlertsBeta = 'c3-enable-product-alerts-beta',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-kiosk-device-expiration-redirect-enabled */
  c3KioskDeviceExpirationRedirectEnabled = 'c3-kiosk-device-expiration-redirect-enabled',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/comm-3-multiple-thematic-rows */
  comm3MultipleThematicRows = 'comm-3-multiple-thematic-rows',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-3-inventory-analytics-tab */
  c3InventoryAnalyticsTab = 'c-3-inventory-analytics-tab',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-mid-grid-ad-row */
  c3MidGridAdRow = 'c3-mid-grid-ad-row',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-new-products-menu-row-template */
  c3NewProductsMenuRowTemplate = 'c3-new-products-menu-row-template',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/c3-specials-redesign-cart
   * @knipignore
   */
  c3SpecialsRedesignCart = 'c3-specials-redesign-cart',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/c3-specials-redesign-menu
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-specials-redesign-menu
   */
  c3SpecialsRedesignMenu = 'c3-specials-redesign-menu',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-boost-apple-oauth */
  c3BoostAppleOauth = 'c3-boost-apple-oauth',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-3-medical-id-expiration-field */
  c3MedicalIdExpirationField = 'c-3-medical-id-expiration-field',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-marketplace-med-id-expiration */
  c3MarketplaceMedIdExpiration = 'c3-marketplace-med-id-expiration',
  /** @see https://app.launchdarkly.com/projects/default/flags/gg-one-click-checkout */
  ggOneClickCheckout = 'gg-one-click-checkout',
  /** @see https://app.launchdarkly.com/projects/default/flags/gg-iframe-redirects */
  iframeRedirects = 'gg-iframe-redirects',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-audit-logs-beta */
  scAuditLogsBeta = 'sc-audit-logs-beta',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sour-chiesel-store-specific-sms-integration-ui */
  scSmsIntegration = 'sour-chiesel-store-specific-sms-integration-ui',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/sc-notification-settings-card-access
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-notification-settings-card-access
   */
  scNotificationSettingsCardAccess = 'sc-notification-settings-card-access',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-stacking-settings-for-pos-stores */
  scStackingSettingsPosStores = 'sc-stacking-settings-for-pos-stores',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-weight-values-frontend */
  scWeightValuesFrontend = 'sc-weight-values-frontend',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-display-product-non-standard-weight */
  scDisplayProductNonStandardWeight = 'sc-display-product-non-standard-weight',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-rewards-line-item */
  c3RewardsLineItem = 'c3-rewards-line-item',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-hide-jane-pay-on-kiosk */
  c3HideJanePayOnKiosk = 'c3-hide-jane-pay-on-kiosk',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-alias-clarification */
  c3AliasClarification = 'c3-alias-clarification',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/batch-product-detail-fetch */
  c3BatchProductDetailFetch = 'c3-batch-product-detail-fetch',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-use-v3-cart */
  c3UseV3Cart = 'c3-use-v3-cart',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-use-v3-store-specials */
  c3UseV3StoreSpecials = 'c3-use-v3-store-specials',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-enable-kiosk-qr-code-auth-with-external-url */
  c3EnableKioskQRCodeAuthWithExternalURL = 'c3-enable-kiosk-qr-code-auth-with-external-url',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-enable-kiosk-qr-code-auth-with-external-url-business */
  c3EnableKioskQRCodeAuthWithExternalURLBusiness = 'c3-enable-kiosk-qr-code-auth-with-external-url-business',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-one-click-add-to-bag */
  c3OneClickAddToBag = 'c3-one-click-add-to-bag',
  /**
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-qualified-group-med-id
   * @see https://app.launchdarkly.com/projects/default/flags/c3-qualified-group-med-id
   */
  c3QualifiedGroupMedId = 'c3-qualified-group-med-id',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-kiosk-pay-by-link */
  c3KioskPayByLink = 'c3-kiosk-pay-by-link',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-carousel-image-fix */
  c3CarouselImageFix = 'c3-carousel-image-fix',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-enable-user-delete */
  c3EnableUserDelete = 'c3-enable-user-delete',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/c3-show-total-terpene-percentage
   * @knipignore
   */
  c3ShowTotalTerpenePercentage = 'c3-show-total-terpene-percentage',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-skip-extraneous-mfa */
  c3SkipExtraneousMfa = 'c3-skip-extraneous-mfa',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-pay-by-bank-ui */
  c3PayByBankUI = 'c3-pay-by-bank-ui',
  /**
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-multiple-condition-specials,
   * @see https://app.launchdarkly.com/projects/default/flags/c3-multiple-condition-specials
   */
  c3MultipleConditionSpecials = 'c3-multiple-condition-specials',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-pos-tags-menu-row-template */
  c3PosTagsMenuRowTemplate = 'c3-pos-tags-menu-row-template',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-filter-on-row-id */
  c3FilterOnRowId = 'c3-filter-on-row-id',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-jane-pay-200k-experiment */
  c3JanePay200kExperiment = 'c3-jane-pay-200k-experiment',
  /**
   * @see https://app.launchdarkly.com/projects/jane-brand/flags/c3-enable-gold-paid-reviews
   * @see https://app.launchdarkly.com/projects/default/flags/c3-enable-gold-paid-reviews
   */
  c3EnableGoldPaidReviews = 'c3-enable-gold-paid-reviews',

  // ---Commerce 1 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-compare-products */
  c1CompareProducts = 'c1-compare-products',
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-kiosk-agreements */
  c1KioskAgreements = 'c1-kiosk-agreements',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-extended-deals-labels */
  c1ExtendedDealsLabels = 'c1-extended-deals-labels',
  /**
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-specials-ranking
   * @see https://app.launchdarkly.com/projects/default/flags/c1-specials-ranking
   */
  c1SpecialsRanking = 'c1-specials-ranking',
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-bundle-tag */
  c1BundleTag = 'c1-bundle-tag',
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-curbside-fulfillment */
  c1CurbsideFulfillment = 'c1-curbside-fulfillment',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-fulfillment-enabled */
  c1FulfillmentV2 = 'c1-fulfillment-enabled',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-hidden-weight-filter/targeting */
  c1HiddenWeightFilter = 'c1-hidden-weight-filter',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-gold-filter/targeting */
  c1GoldFilter = 'c1-gold-filter',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-biz-dash-sso-login */
  c1BizDashSsoLogin = 'c1-biz-dash-sso-login',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-permission-gate */
  c1PermissionGate = 'c1-permission-gate',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-geofence-delivery-hours */
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-geofence-delivery-hours */
  c1GeofenceDeliveryHours = 'c1-geofence-delivery-hours',

  // ---Commerce 2 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-no-empty-posts */
  c2NoEmptyPosts = 'c2-no-empty-posts',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-2-premium-cloudflare-integrations */
  c2PremiumCloudflareIntegrations = 'c-2-premium-cloudflare-integrations',
  /**
   *  @see https://app.launchdarkly.com/projects/default/flags/c2-embedded-row-add-to-bag
   *  @see https://app.launchdarkly.com/projects/jane-manager/flags/c2-embedded-row-add-to-bag
   */
  c2EmbeddedRowAddToBag = 'c2-embedded-row-add-to-bag',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-bloom-mixpanel-reidentify */
  c2BloomMixpanelReidentify = 'c2-bloom-mixpanel-reidentify',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-disable-ga-in-bloom-headless */
  c2DisableGaInBloomHeadless = 'c2-disable-ga-in-bloom-headless',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-gtag-events */
  obGtagEvents = 'ob-gtag-events',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/ob-customize-premium-footer */
  obCustomizePremiumFooter = 'ob-customize-premium-footer',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/ob-import-premium-footer */
  obImportPremiumFooter = 'ob-import-premium-footer',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-wizard-banner-enabled */
  obWizardBannerEnabled = 'ob-wizard-banner-enabled',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-wizard-skip-intro */
  c2WizardSkipIntro = 'c2-wizard-skip-intro',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-custom-category-images */
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c2-custom-category-images */
  c2CustomCategoryImages = 'c2-custom-category-images',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c2-custom-font-import */
  c2CustomFontImport = 'c2-custom-font-import',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c2-category-on-page-seo */
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-category-on-page-seo */
  c2CategoryOnPageSeo = 'c2-category-on-page-seo',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-leave-review */
  c2LeaveReview = 'c2-leave-review',
  /**
   * Leave Review A/B test variant, only read while `c2LeaveReview` is on.
   * On: step-by-step wizard modal. Off: single form modal.
   * @see https://app.launchdarkly.com/projects/default/flags/c2-leave-review-wizard
   */
  c2LeaveReviewWizard = 'c2-leave-review-wizard',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/c2-boost-review-flow
   * @knipignore consumed by the prompt and modal, in progress
   */
  c2BoostReviewFlow = 'c2-boost-review-flow',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-product-favorites */
  c2ProductFavorites = 'c2-product-favorites',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-compare-intro */
  c2CompareIntro = 'c2-compare-intro',
  /** @see https://app.launchdarkly.com/projects/default/flags/c2-premium-cart-drawer */
  c2PremiumCartDrawer = 'c2-premium-cart-drawer',

  // ---DM Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/dm-stack-adapt-frontend-pixel-stores-enabled */
  dmStackAdaptStoresEnabled = 'dm-stack-adapt-frontend-pixel-stores-enabled',
  /** @see https://app.launchdarkly.com/projects/default/flags/dm-auto-refresh-enabled */
  dmAutoRefreshEnabled = 'dm-auto-refresh-enabled',

  // ---CRM Flag---
  /** @see https://app.launchdarkly.com/projects/default/flags/bb-crm-tracking */
  bbCrmTracking = 'bb-crm-tracking',

  // ---Platform 2 Flags---
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p2-minimum-spend-for-discounts-ui */
  p2MinimumSpendForDiscountsUi = 'p2-minimum-spend-for-discounts-ui',
  // ---Platform 1 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/p1-headless-privacy-mode */
  p1HeadlessPrivacyMode = 'p1-headless-privacy-mode',
  /** @see https://app.launchdarkly.com/projects/default/flags/p1-skip-recaptcha-for-sandbox-stores */
  p1SkipRecaptchaForSandboxStores = 'p1-skip-recaptcha-for-sandbox-stores',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p1-tipping-curbside */
  p1TippingCurbside = 'p-1-tipping-curbside',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p1-fee-taxes */
  p1FeeTaxes = 'p1-fee-taxes',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p1-place-image-ad-second */
  p1PlaceImageAdSecond = 'p1-place-image-ad-second',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p1-display-price-for-image-ads */
  p1DisplayPriceForImageAds = 'p1-display-price-for-image-ads',
  /** @see https://app.launchdarkly.com/projects/default/flags/p1-california-prop-65-warnings */
  p1CaliforniaProp65Warnings = 'p1-california-prop-65-warnings',
}

export type FeatureFlag = (typeof FLAGS)[keyof typeof FLAGS];
